import { useEffect, useState } from "react";
import { Main } from "@/components/Main";
import { PageHeader } from "@/components/PageHeader";
import { CreateLoaderTable } from "@/components/Loaders";
import { cn } from "@/lib/utils";
import { colors } from "@/utils/colors";
import { getMyAbsences, getMyPendingAbsenceApprovals } from "@/Api/absences";
import { AbsenceStatusConst, AbsenceStatusLabel } from "@/types/absence";
import { RequestAbsenceBtn } from "@/App/Absences/Request";
import { CancelAbsenceBtn } from "@/App/Absences/Cancel";
import { DecideAbsenceBtns } from "@/App/Absences/Decide";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import { MoreHorizontal, CalendarOff, AlertTriangle } from "lucide-react";

const formatDate = (value?: string | null) =>
  value ? value.slice(0, 10).split("-").reverse().join("/") : "-";

const formatPeriod = (item: any): string => {
  if (item.start_time && item.end_time) {
    return `${formatDate(item.start_date)} ${item.start_time.slice(0, 5)}-${item.end_time.slice(0, 5)}`;
  }
  if (item.start_date === item.end_date) {
    return formatDate(item.start_date);
  }
  return `${formatDate(item.start_date)} a ${formatDate(item.end_date)}`;
};

export const PageList = () => {
  const [listData, setListData] = useState<any>({});
  const [state, setState] = useState("loading");
  const [pendingApprovals, setPendingApprovals] = useState<any[]>([]);

  const fetchPendingApprovals = async () => {
    const { data } = await getMyPendingAbsenceApprovals();
    setPendingApprovals(data?.response?.data ?? []);
  };

  const fetchData = async (page: number = 1) => {
    setState("loading");
    const { data } = await getMyAbsences(page);
    setListData(data?.response ?? {});
    setState("production");
  };

  useEffect(() => {
    fetchData();
    fetchPendingApprovals();
  }, []);

  const refetchAll = () => {
    fetchPendingApprovals();
    fetchData(listData.current_page || 1);
  };

  const pageActions = {
    next: async () => {
      if (listData?.current_page < listData?.last_page) {
        await fetchData(listData.current_page + 1);
      }
    },
    previous: async () => {
      if (listData?.current_page > 1) {
        await fetchData(listData.current_page - 1);
      }
    },
    lastPage: async () => {
      if (listData?.current_page !== listData?.last_page) {
        await fetchData(listData.last_page);
      }
    },
  };

  return (
    <Main>
      <PageHeader
        icon={CalendarOff}
        title="Meus Abonos"
        description="Solicite e acompanhe seus abonos (atestados, licenças e outras ausências justificadas)"
      >
        <RequestAbsenceBtn onCreated={() => fetchData(1)} />
      </PageHeader>

      {pendingApprovals.length > 0 && (
        <div className="flex flex-col gap-2 mb-4 rounded-md border border-amber-500/40 bg-amber-500/10 p-4">
          <label className="text-sm font-medium flex items-center gap-1 text-amber-600 dark:text-amber-400">
            <AlertTriangle className="size-4" /> Aguardando sua aprovação
          </label>
          {pendingApprovals.map((item: any) => (
            <div
              key={item.uuid}
              className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 rounded-md border p-3 bg-zinc-50 dark:bg-[#2c2c2c]"
            >
              <div className="text-sm">
                <div className="font-semibold">
                  {item.absence_type?.name} ({formatPeriod(item)})
                </div>
                <div className="text-muted-foreground">
                  Solicitado por {item.requested_by?.name}: {item.justification}
                </div>
              </div>
              <DecideAbsenceBtns uuid={item.uuid} onDecided={refetchAll} />
            </div>
          ))}
        </div>
      )}

      {state === "loading" ? (
        <CreateLoaderTable cols={5} lines={6} />
      ) : (
        <>
          <Table className="bg-zinc-50 rounded dark:bg-[#2c2c2c]">
            <TableHeader>
              <TableRow>
                <TableHead>Tipo</TableHead>
                <TableHead>Período</TableHead>
                <TableHead>Justificativa</TableHead>
                <TableHead className="text-center">Status</TableHead>
                <TableHead className="text-center">Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {listData?.data?.map((item: any) => (
                <TableRow key={item.uuid}>
                  <TableCell>{item.absence_type?.name}</TableCell>
                  <TableCell>{formatPeriod(item)}</TableCell>
                  <TableCell className="truncate max-w-80">
                    {item.justification}
                  </TableCell>
                  <TableCell className="text-center">
                    <Badge
                      variant={
                        item.status === AbsenceStatusConst.APPROVED
                          ? "default"
                          : item.status === AbsenceStatusConst.REJECTED ||
                              item.status === AbsenceStatusConst.CANCELLED
                            ? "destructive"
                            : "outline"
                      }
                    >
                      {AbsenceStatusLabel[item.status]}
                    </Badge>
                  </TableCell>
                  <TableCell className="flex justify-center">
                    {item.status === AbsenceStatusConst.PENDING &&
                      item.requested_by_role === "employee" && (
                        <CancelAbsenceBtn uuid={item.uuid} onCancelled={refetchAll} />
                      )}
                  </TableCell>
                </TableRow>
              ))}
              {(!listData?.data || listData.data.length < 1) && (
                <TableRow>
                  <TableCell colSpan={5}>Nada foi encontrado.</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          <div className="flex justify-between mb-3 mt-2">
            {listData?.data?.length > 0 && (
              <span className="font-medium">
                Exibindo {listData?.current_page} de um total de{" "}
                {listData?.last_page}
              </span>
            )}
            {listData?.last_page > 1 && (
              <Pagination>
                <PaginationContent>
                  <PaginationItem>
                    <PaginationPrevious
                      href="#"
                      className={cn("h-8", colors.bg_primary)}
                      onClick={pageActions.previous}
                    />
                  </PaginationItem>
                  <PaginationItem>
                    <PaginationLink
                      href="#"
                      className="size-8 bg-transparent border-2 border-[#023859] text-slate-900 font-bold cursor-auto hover:bg-transparent"
                    >
                      <span className="pointer-events-none dark:text-[#F4F4F5]">
                        {listData?.current_page}
                      </span>
                    </PaginationLink>
                  </PaginationItem>
                  <PaginationItem>
                    <PaginationLink
                      href="#"
                      className="text-black h-8 hover:bg-stone-950/10"
                      onClick={pageActions.lastPage}
                    >
                      <MoreHorizontal className="h-4 w-4 dark:text-[#F4F4F5]" />
                    </PaginationLink>
                  </PaginationItem>
                  <PaginationItem>
                    <PaginationNext
                      href="#"
                      className={cn("h-8", colors.bg_primary)}
                      onClick={pageActions.next}
                    />
                  </PaginationItem>
                </PaginationContent>
              </Pagination>
            )}
          </div>
        </>
      )}
    </Main>
  );
};

export default PageList;
